import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import charactersData from '../story/the-missing-hour/characters_data.json';
import { interviewApi } from '../services/interviewApi';
import Header from './Header';

const Interview = () => {
  const { suspectId } = useParams();
  const navigate = useNavigate();
  const [question, setQuestion] = useState('');
  const [interviewLog, setInterviewLog] = useState([]);
  const [timeRemaining, setTimeRemaining] = useState(600); // 10 minutes in seconds
  const [isInterviewActive, setIsInterviewActive] = useState(false);
  const [currentScene, setCurrentScene] = useState('initial');
  const [isLoadingResponse, setIsLoadingResponse] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [useRealAPI, setUseRealAPI] = useState(true);
  const [isTyping, setIsTyping] = useState(false);
  const [currentTypingText, setCurrentTypingText] = useState('');
  const [currentTypingIndex, setCurrentTypingIndex] = useState(0);
  const [dramaticTiming, setDramaticTiming] = useState([]);
  const [emotionalIntensity, setEmotionalIntensity] = useState(5);
  const [thinkingPatterns, setThinkingPatterns] = useState([]);
  const chatMessagesRef = useRef(null);
  
  const suspect = charactersData.characters.find(char => char.id === suspectId);
  
  // Timer countdown
  useEffect(() => {
    if (isInterviewActive && timeRemaining > 0) {
      const timer = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            endInterview();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      return () => clearInterval(timer);
    }
  }, [isInterviewActive, timeRemaining]);

  // Auto-scroll chat messages to bottom (not the entire window)
  useEffect(() => {
    if (chatMessagesRef.current) {
      chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
    }
  }, [interviewLog]);

  // Auto-scroll during typewriter effect to keep chat at bottom
  useEffect(() => {
    if (isTyping && chatMessagesRef.current) {
      chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
    }
  }, [isTyping, currentTypingIndex]);

  // Enhanced typewriter effect with dramatic pauses and thinking patterns from API
  useEffect(() => {
    if (isTyping && currentTypingText && currentTypingIndex < currentTypingText.length) {
      const currentChar = currentTypingText[currentTypingIndex];
      const nextChar = currentTypingText[currentTypingIndex + 1];
      
      // Calculate delay based on character, context, and API dramatic elements
      let delay = 30; // Base typing speed
      
      // Adjust base speed based on emotional intensity from API
      if (emotionalIntensity >= 8) {
        delay = 20; // Fast typing when highly emotional
      } else if (emotionalIntensity <= 3) {
        delay = 50; // Slower typing when calm/thoughtful
      }
      
      // Check for API dramatic timing markers
      const currentPosition = currentTypingIndex;
      const hasDramaticMarker = dramaticTiming.some(marker => 
        marker.position === currentPosition && marker.type === 'pause'
      );
      
      if (hasDramaticMarker) {
        delay = 600; // Extended pause for dramatic effect
      }
      
      // Check for thinking patterns from API
      const hasThinkingPattern = thinkingPatterns.some(pattern => 
        pattern.position === currentPosition
      );
      
      if (hasThinkingPattern) {
        delay = 800; // Extended pause for thinking
      }
      
      // Enhanced dramatic pauses for punctuation and special markers
      if (currentChar === '.') {
        // Check if this is part of "..." (thinking pause)
        if (nextChar === '.' && currentTypingText[currentTypingIndex + 2] === '.') {
          delay = 1000; // Extended pause for thinking
        } else {
          delay = 300; // Regular sentence end pause
        }
      } else if (currentChar === '!') {
        delay = 400; // Exclamation pause
      } else if (currentChar === '?') {
        delay = 350; // Question pause
      } else if (currentChar === ',') {
        delay = 200; // Comma pause
      } else if (currentChar === ';') {
        delay = 250; // Semicolon pause
      } else if (currentChar === ':') {
        delay = 300; // Colon pause
      } else if (currentChar === '-') {
        delay = 150; // Dash pause
      } else if (currentChar === '(' || currentChar === ')') {
        delay = 200; // Parentheses pause
      }
      
      // Check for custom dramatic markers in the text
      if (currentChar === '|' && nextChar === '|') {
        delay = 500; // Emotional pause
      } else if (currentChar === ':' && nextChar === ':') {
        delay = 400; // Emphasis pause
      } else if (currentChar === '-' && nextChar === '-') {
        delay = 350; // Dramatic break
      }
      
      // Additional pauses for dramatic effect
      if (currentChar === ' ' && nextChar && nextChar === nextChar.toUpperCase()) {
        delay = 150; // Pause before capital letters (new thoughts)
      }
      
      // Random variation to mimic human typing (reduced for more controlled drama)
      delay += Math.random() * 30 - 15; // ±15ms variation
      
      const timer = setTimeout(() => {
        setCurrentTypingIndex(prev => prev + 1);
      }, delay);
      
      return () => clearTimeout(timer);
    } else if (isTyping && currentTypingIndex >= currentTypingText.length) {
      // Add the completed response to the interview log
      setInterviewLog(prev => [...prev, { 
        type: 'response', 
        content: currentTypingText, 
        timestamp: new Date().toLocaleTimeString(), 
        timeRemaining: timeRemaining 
      }]);
      
      setIsTyping(false);
      setCurrentTypingIndex(0);
      setCurrentTypingText('');
      setDramaticTiming([]);
      setEmotionalIntensity(5);
      setThinkingPatterns([]);
    }
  }, [isTyping, currentTypingText, currentTypingIndex, timeRemaining, dramaticTiming, emotionalIntensity, thinkingPatterns]);

  // Test API connection on mount
  useEffect(() => {
    const testAPI = async () => {
      try {
        const result = await interviewApi.testConnection();
  
      } catch (error) {
        console.warn('⚠️ API connection failed, using fallback responses:', error);
        setUseRealAPI(false);
      }
    };
    
    testAPI();
  }, []);

  if (!suspect) {
    return <div>Suspect not found</div>;
  }

  const startInterview = () => {
    setIsInterviewActive(true);
    addSceneDescription('The interview room is stark and clinical. Bright overhead lights cast harsh shadows. The suspect sits across from you, visibly nervous. The air feels thick with tension.');
  };

  const endInterview = () => {
    setIsInterviewActive(false);
    addSceneDescription('INTERVIEW TERMINATED. Time limit reached. Interview session concluded. Officer must now review transcript and evaluate evidence gathered.');
  };

  const addSceneDescription = (description) => {
    const newEntry = {
      type: 'scene',
      content: description,
      timestamp: new Date().toLocaleTimeString(),
      timeRemaining: timeRemaining
    };
    setInterviewLog(prev => [...prev, newEntry]);
  };

  const addQuestion = (questionText) => {
    const newEntry = {
      type: 'question',
      content: questionText,
      timestamp: new Date().toLocaleTimeString(),
      timeRemaining: timeRemaining
    };
    setInterviewLog(prev => [...prev, newEntry]);
    
    // Simulate suspect response and behavioral cues
    setTimeout(() => {
      generateSuspectResponse(questionText);
    }, 1000);
  };

  const startTypewriterEffect = (text, timing = [], intensity = 5, patterns = []) => {
    setCurrentTypingText(text);
    setCurrentTypingIndex(0);
    setDramaticTiming(timing);
    setEmotionalIntensity(intensity);
    setThinkingPatterns(patterns);
    setIsTyping(true);
  };

  const generateSuspectResponse = async (questionText) => {
    if (!useRealAPI) {
      // Fallback to mock responses if API is unavailable
      generateMockResponse(questionText);
      return;
    }

    setIsLoadingResponse(true);
    setApiError(null);

    try {
      // Prepare interview data using character persona
      const interviewData = {
        suspect_id: suspect.id,
        question: questionText,
        interview_context: interviewApi.buildInterviewContext(suspect),
        previous_questions: interviewApi.buildPreviousQuestions(interviewLog),
        suspect_personality: `${suspect.personality}. ${suspect.behaviour_in_interview}. ${interviewApi.getPersonaPrompt(suspect)}`
      };

      // Call the ChatGPT API
      const result = await interviewApi.conductInterview(interviewData);
      
      if (result.success) {
        const { response, behavior, emotional_state, credibility_indicators, suggested_follow_up } = result.data;
        
        // Add behavioral observation first
        setInterviewLog(prev => [...prev, { 
          type: 'behavior', 
          content: behavior, 
          timestamp: new Date().toLocaleTimeString(), 
          timeRemaining: timeRemaining 
        }]);
        
        // Start typewriter effect for the response with dramatic elements
        setTimeout(() => {
          startTypewriterEffect(
            response, 
            result.data.dramatic_timing || [],
            result.data.emotional_intensity || 5,
            result.data.thinking_patterns || []
          );
        }, 1000);
        
      } else {
        throw new Error(result.message || 'Interview failed');
      }
      
    } catch (error) {
      setApiError(`Interview failed: ${error.message}`);
      
      // Fallback to mock response
      generateMockResponse(questionText);
    } finally {
      setIsLoadingResponse(false);
    }
  };

  const generateMockResponse = (questionText) => {
    // Fallback mock response generation with enhanced dramatic pauses
    let response = '';
    let behavior = '';
    
    if (questionText.toLowerCase().includes('victim') || questionText.toLowerCase().includes('ellie')) {
      response = "I... I don't know what happened to her. We were friends, that's all. I mean... I can't even imagine...";
      behavior = "The suspect becomes visibly uncomfortable, fidgeting with their hands and avoiding eye contact.";
    } else if (questionText.toLowerCase().includes('alibi') || questionText.toLowerCase().includes('where')) {
      response = "I was at home that night. Alone. I can't prove it, but... that's the truth. I swear it.";
      behavior = "The suspect's voice wavers slightly, and they shift in their chair repeatedly.";
    } else if (questionText.toLowerCase().includes('relationship') || questionText.toLowerCase().includes('feelings')) {
      response = "We had a... complicated relationship. There were... misunderstandings. Things got... messy.";
      behavior = "The suspect's jaw clenches, and they grip the armrests of the chair tightly.";
    } else if (questionText.toLowerCase().includes('evidence') || questionText.toLowerCase().includes('proof')) {
      response = "Look, I don't know what evidence you think you have, but... I'm innocent! I didn't do anything wrong!";
      behavior = "The suspect's breathing becomes rapid, and they lean forward aggressively.";
    } else {
      response = "I don't have anything to hide. I'm telling you the truth. Why won't you believe me?";
      behavior = "The suspect maintains steady eye contact but their breathing is noticeably rapid.";
    }
    
    // Add behavioral observation first
    setInterviewLog(prev => [...prev, {
      type: 'behavior',
      content: behavior,
      timestamp: new Date().toLocaleTimeString(),
      timeRemaining: timeRemaining
    }]);
    
    // Start typewriter effect for the response with dramatic elements
    setTimeout(() => {
      startTypewriterEffect(
        response, 
        [], // No dramatic timing for mock responses
        7, // Medium-high emotional intensity for mock responses
        [] // No thinking patterns for mock responses
      );
    }, 1000);
  };

  const handleSubmitQuestion = (e) => {
    e.preventDefault();
    if (question.trim() && isInterviewActive) {
      addQuestion(question.trim());
      setQuestion('');
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleEndInterview = () => {
    endInterview();
  };

  const handleBackToSuspect = () => {
    navigate(`/suspect/${suspectId}`);
  };

  return (
    <div 
      className="interview-container"
      style={{
        backgroundImage: 'url(/images/interview-room.jpeg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      <Header />
      
      <div className="interview-content">
        <div className="interview-header">
          <button className="back-button" onClick={handleBackToSuspect}>
            ← Back to Suspect
          </button>
          
          <div className="suspect-info">
            <h2>Interviewing</h2>
            <div className="suspect-name">{suspect.name}</div>
            <div className="suspect-role">{suspect.role}</div>
            {!useRealAPI && (
              <div className="api-status">⚠️ Using offline mode</div>
            )}
          </div>
          
          <div className="timer-section">
            {!isInterviewActive ? (
              <button 
                className="start-interview-btn"
                onClick={startInterview}
                disabled={timeRemaining === 0}
              >
                {timeRemaining === 0 ? 'INTERVIEW ENDED' : 'START INTERVIEW'}
              </button>
            ) : (
              <div className="timer-display">
                <div className="timer-label">Time Remaining:</div>
                <div className={`timer ${timeRemaining <= 60 ? 'timer-warning' : ''}`}>
                  {formatTime(timeRemaining)}
                </div>
                <button 
                  className="end-interview-btn"
                  onClick={handleEndInterview}
                >
                  END INTERVIEW
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="interview-instructions">
          <div className="instructions-content">
            <h3>INTERVIEW PROTOCOL</h3>
            <p>This interview session is limited to 10 minutes. Use your time efficiently to gather relevant information. Document all responses and behavioral observations for case analysis.</p>
          </div>
        </div>

        <div className="interview-chat">
          {interviewLog.length > 0 && (
            <div className="chat-messages" ref={chatMessagesRef}>
              {interviewLog.map((entry, index) => (
                <div key={index} className={`chat-entry ${entry.type}`}>
                  <div className="entry-header">
                    <span className="entry-type">{entry.type.toUpperCase()}</span>
                    <span className="entry-time">{entry.timestamp}</span>
                    <span className="entry-timer">{formatTime(entry.timeRemaining)}</span>
                  </div>
                  <div className="entry-content">{entry.content}</div>
                </div>
              ))}
              
              {isLoadingResponse && (
                <div className="chat-entry loading">
                  <div className="entry-header">
                    <span className="entry-type">THINKING</span>
                    <span className="entry-time">{new Date().toLocaleTimeString()}</span>
                    <span className="entry-timer">{formatTime(timeRemaining)}</span>
                  </div>
                  <div className="entry-content">
                    <em>The suspect is considering their response...</em>
                    <span className="thinking-dots">...</span>
                  </div>
                </div>
              )}
              
              {isTyping && (
                <div className="chat-entry response typing">
                  <div className="entry-header">
                    <span className="entry-type">RESPONSE</span>
                    <span className="entry-time">{new Date().toLocaleTimeString()}</span>
                    <span className="entry-timer">{formatTime(timeRemaining)}</span>
                  </div>
                  <div className="entry-content">
                    {currentTypingText.substring(0, currentTypingIndex)}
                    <span className="typing-cursor">|</span>
                  </div>
                </div>
              )}
              
              {apiError && (
                <div className="chat-entry error">
                  <div className="entry-header">
                    <span className="entry-type">SYSTEM</span>
                    <span className="entry-time">{new Date().toLocaleTimeString()}</span>
                  </div>
                  <div className="entry-content">
                    ⚠️ {apiError} (Using fallback response)
                  </div>
                </div>
              )}
              

            </div>
          )}
          
          {isInterviewActive && (
            <form className="question-input" onSubmit={handleSubmitQuestion}>
              <textarea
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                placeholder="Type your question here..."
                className="question-field"
                disabled={!isInterviewActive}
                rows={3}
              />
              <button 
                type="submit" 
                className="ask-question-btn"
                disabled={!question.trim() || !isInterviewActive}
              >
                ASK QUESTION
              </button>
            </form>
          )}
        </div>

        <div className="interview-notes">
          <h3>Interview Notes</h3>
          <p>Pay attention to behavioral cues, inconsistencies, and emotional responses. 
             Use this information to build your case.</p>
        </div>
      </div>
    </div>
  );
};

export default Interview;
